import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
  CartErrorEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';



/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);

    // Initial cart verification on page load
    this.#verifyCart();

    // Cutting Instructions listeners
    this.addEventListener('change', this.#handleCuttingChange.bind(this));
    this.addEventListener('input', this.#handleCuttingInput.bind(this));
    this.#checkCuttingOrderState();
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    
    this.removeEventListener('change', this.#handleCuttingChange.bind(this));
    this.removeEventListener('input', this.#handleCuttingInput.bind(this));
  }

  /**
   * Verify Cart items against Spicers API to ensure prices and HMACs match the current logged in user.
   */
  #verifyCart() {
    window.fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        console.log('[Cart] Shopify /cart.js Response:', cart);
        if (!cart || !cart.items || cart.items.length === 0) return;
        let line = 0;
        const data = cart.items.map(i => {
          return {
            line: ++line,
            cust_price: i.properties?._cust_price,
            price: i.properties?._price,
            hash: i.properties?._hmac
          };
        });
        const theme = window.theme;
        if (!theme || !theme.ENV || !theme.ENV.apiUrl) return;

        window.fetch(`${theme.ENV.apiUrl}/cart/verify`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'ngrok-skip-browser-warning': '1'
          },
          body: JSON.stringify(data)
        })
        .then(res => res.json())
        .then(result => {
           console.log('[Cart] Spicers /cart/verify Response:', result);
           if (result && !result.valid) {
             const checkoutBtns = document.querySelectorAll('[name="checkout"], .cart__checkout-button');
             checkoutBtns.forEach(btn => btn.setAttribute('disabled', 'disabled'));

             result.invalidLine.forEach(l => {
                this.#handleCartError(l, { errors: 'Account changed. Please update quantity to re-price.' });
             });
           }
        })
        .catch(console.error);
      });
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * Validates MOQ (using variant.metafields.custom.order_multiple, same source as Cart Page)
   * and LM (lineal metre) rules before committing any quantity change.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  async #onQuantityChange(event) {
    const { quantity, cartLine: line } = event.detail;

    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    // Find the quantity input for this line to read Cart-Page-compatible data attributes
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    const input = quantitySelector?.querySelector('input[ref="quantityInput"], input[data-cart-line]');
    const material = input?.dataset?.material;
    const prm = input?.dataset?.prm;
    const itemKey = input?.dataset?.itemKey;

    // --- MOQ validation (mirrors Cart Page blur handler exactly) ---
    const hasMultiplierRestriction = input?.dataset?.restrictQty !== undefined;
    const multiplier = hasMultiplierRestriction ? (parseFloat(input.dataset.multiplier) || 1) : 1;
    const lmRaw = input?.dataset?.lm ? parseFloat(input.dataset.lm) : 0;
    const isLmProduct = lmRaw > 0;
    // lastInput: last server-confirmed qty — used to revert input when user cancels popup
    const lastInput = parseFloat(input?.dataset?.lastInput) || quantity;

    const safeQty = Math.round(quantity * 1000);
    const safeMult = Math.round(multiplier * 1000);
    const isViolation = (multiplier > 1 && safeQty % safeMult !== 0);

    const applyQty = (qty) => {
      if (input) { input.value = qty; input.dataset.lastInput = qty; }
      if (material && prm) {
        this.#updateSpicersQuantity({ line, quantity: qty, material, prm, itemKey });
      } else {
        this.updateQuantity({ line, quantity: qty, action: 'change' });
      }
      const lineItemRow = this.refs.cartItemRows?.[line - 1];
      if (lineItemRow) {
        const textComponent = lineItemRow.querySelector('text-component');
        textComponent?.shimmer();
      }
    };

    if (isViolation) {
      let nextQty = Math.ceil(safeQty / safeMult) * multiplier;
      if (isLmProduct) nextQty = +nextQty.toFixed(2);

      const msg = `Order quantity must be in multiples of ${multiplier} for this product - would you like to update order quantity to ${nextQty}?`;

      this.#showMoqConfirm(msg, (accepted) => {
        if (accepted) {
          this.#checkStockStatusAndApply(nextQty, material, prm, itemKey, line, lastInput, applyQty);
        } else {
          if (input) input.value = lastInput;
        }
      });
      return;
    }
    // --- End MOQ validation ---

    // --- Stock Validation ---
    this.#checkStockStatusAndApply(quantity, material, prm, itemKey, line, lastInput, applyQty);
  }

  async #checkStockStatusAndApply(quantity, material, prm, itemKey, line, lastInput, applyQty) {
    if (!material) {
       applyQty(quantity);
       return;
    }

    const input = this.refs.quantitySelectors?.[line - 1]?.querySelector('input');
    const theme = window.theme;
    if (!theme || !theme.ENV || !theme.ENV.apiUrl) {
       applyQty(quantity);
       return;
    }

    const state = theme.utils?.getCookie('user-state') || 'NSW';
    const customer = window.customer?.sapId || '';
    const url = `${theme.ENV.apiUrl}/stock-template-by-material?materialNo=${material}&qty=${quantity}&postcode=${state}${customer ? `&customer=${customer}` : ''}`;

    const wrapperSelector = 'td, .cart-item__quantity-wrapper, .cart-items__quantity, .quantity-selector';
    if (input) input.closest(wrapperSelector)?.classList.add('loading');

    try {
      const response = await window.fetch(url, { headers: { 'ngrok-skip-browser-warning': '1' } });
      const result = await response.json();
      if (input) input.closest(wrapperSelector)?.classList.remove('loading');

      const soh = result?.data?.soh ?? result?.soh ?? 0;
      if (soh < quantity) {
         // BACKORDER POPUP DISABLED:
         // The customer service team picks it up, contacts the customer, and flags that the item needs to be backordered.
         applyQty(quantity);
         this.#recalcCuttingSheets(line, quantity);
         return;
      }
      applyQty(quantity);
      this.#recalcCuttingSheets(line, quantity);
    } catch (err) {
      console.error(err);
      if (input) input.closest(wrapperSelector)?.classList.remove('loading');
      applyQty(quantity); // proceed anyway on network error
      this.#recalcCuttingSheets(line, quantity);
    }
  }

  /**
   * Calls the Spicers pricing API to get a fresh HMAC for the updated quantity,
   * then updates the cart line properties (qty + _hmac) via /cart/change.js.
   * Shopify's line quantity stays at 1 — the real qty lives in properties.qty.
   *
   * @param {Object} config
   * @param {number} config.line - 1-indexed cart line
   * @param {number} config.quantity - New qty entered by the user
   * @param {string} config.material - SKU / material number
   * @param {string} config.prm - PRM value
   * @param {string} config.itemKey - Shopify cart item key
   */
  async #updateSpicersQuantity({ line, quantity, material, prm, itemKey }) {
    const theme = window.theme;
    if (!theme) return;

    this.#disableCartItems();

    try {
      const customer = (window.customer?.sapId) || '143282';
      const captchaKey = theme.ENV?.captcha_site_key || '6LextFosAAAAAH3nCeeqSd1g50XqeAfURZOt8Z6a';

      // Get a fresh reCAPTCHA token only if we are not removing the item (quantity > 0)
      let token = '';
      if (quantity > 0) {
        try {
          await new Promise((resolve) => {
            grecaptcha.ready(() => {
              grecaptcha.execute(captchaKey, { action: 'submit' }).then((t) => {
                token = t;
                resolve();
              });
            });
          });
        } catch (e) {
          console.warn('[Cart Drawer] reCAPTCHA failed, skipping pricing API:', e);
        }
      }

      let newProperties = null;
      let newHmac = null;

      if (token && quantity > 0) {
        const apiUrl = theme.ENV?.apiUrl;
        const url = `${apiUrl}/cart?token=${token}`;
        const session = window.ShopifyAnalytics?.lib?.user?.()?.traits?.()?.uniqToken || '';

        const cartData = {
          variants: [{ variantId: null, qty: quantity, material: material, prm: prm }],
          session: session,
          token: 'KIi16KkJ3HEjwydO7S582tKkESRZJsaJDlH2ckDXZ1OJjmHp6W',
          customer: customer
        };

        // Fetch current cart to get the actual variantId for this line
        const cartRes = await window.fetch('/cart.js');
        const cartObj = await cartRes.json();
        const cartItem = cartObj.items[line - 1];
        if (cartItem) {
          cartData.variants[0].variantId = cartItem.variant_id;
          // Preserve the existing cart item properties as the base
          newProperties = { ...(cartItem.properties || {}) };

          const result = await window.fetch(url, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartData)
          }).then(r => r.json());

          const apiItem = Array.isArray(result) ? result[0] : result;
          
          console.log('[Cart] Spicers API Response for Quantity Change:', result);
          console.log('[Cart] Item Data being set:', apiItem);
          
          // Catch Spicers API errors directly so they don't get bypassed silently
          if (apiItem && apiItem.error) {
             throw new Error(apiItem.error);
          }

          if (apiItem && apiItem.properties) {
            Object.entries(apiItem.properties).forEach(([k, v]) => {
              newProperties = newProperties || {};
              newProperties[k] = v;
            });
            newHmac = apiItem.hmac;
          }
        }
      }

      // Build the updated properties block — always update qty regardless of API success
      const finalProperties = {
        ...(newProperties || {}),
        qty: quantity,
      };
      // To prevent showing the old qty alongside _qty
      Object.assign(finalProperties, (newHmac ? { _hmac: newHmac } : {}));

      // Use the full cart item key (variant_id:hash) if available; Shopify requires the complete key string
      let changePayload;
      if (quantity === 0) {
        // If removing the item, we must pass quantity: 0 at the root level to Shopify
        changePayload = itemKey ? { id: itemKey, quantity: 0 } : { line: line, quantity: 0 };
      } else {
        changePayload = itemKey
          ? { id: itemKey, properties: finalProperties }
          : { line: line, properties: finalProperties };
      }

      const changeResponse = await window.fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(changePayload)
      });
      const changeResult = await changeResponse.json();

      if (changeResponse.status === 422 || (changeResult && changeResult.status && changeResult.message)) {
         const errorMsg = changeResult.description || changeResult.message || '';
         if (errorMsg.toLowerCase().includes('different account')) {
            this.#verifyCart();
            return;
         }
         throw new Error(errorMsg);
      }

      // Dispatch cart update event to update header cart count & price dynamically
      const newCartItemCount = changeResult?.items?.length || 0;
      const newCartTotal = changeResult?.total_price || 0;
      this.dispatchEvent(
        new CartUpdateEvent({}, this.sectionId, {
          itemCount: newCartItemCount,
          source: 'cart-items-component',
          carttotal: newCartTotal,
        })
      );

      // Re-render the cart section so displayed qty and price reflect the change
      sectionRenderer.renderSection(this.sectionId, { cache: false });

    } catch (err) {
      console.error('[Cart Drawer] Spicers qty update failed:', err);
      this.#handleCartError(line, { errors: err.message || 'Error updating cart item' });
    } finally {
      this.#enableCartItems();
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows?.[line - 1];

    if (!cartItemRowToRemove) return;

    const remove = () => cartItemRowToRemove.remove();

    if (prefersReducedMotion()) return remove();

    // Add class to the row to trigger the animation
    cartItemRowToRemove.style.setProperty('--row-height', `${cartItemRowToRemove.clientHeight}px`);
    cartItemRowToRemove.classList.add('removing');

    // Remove the row after the animation ends
    onAnimationEnd(cartItemRowToRemove, remove);
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors || parsedResponseText.status >= 400 || parsedResponseText.message) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        this.dispatchEvent(
          new CartUpdateEvent({}, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
            carttotal: parsedResponseText.total_price,
          })
        );

        morphSection(this.sectionId, parsedResponseText.sections[this.sectionId]);
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    // If it's a global 422 Unprocessable Content error, dispatch globally and abort
    if (parsedResponseText.status === 422 || (!parsedResponseText.errors && parsedResponseText.description)) {
       const msg = parsedResponseText.description || parsedResponseText.message;
       
       if (msg && msg.toLowerCase().includes('different account')) {
          this.#verifyCart();
          return;
       }

       if (msg) window.dispatchEvent(new CartErrorEvent(this.id, msg));
       return;
    }

    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput) {
      quantityInput.value = quantityInput.defaultValue;
    }

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    // If line item error containers don't exist (e.g. Cart Drawer), fallback to global error
    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
       window.dispatchEvent(new CartErrorEvent(this.id, parsedResponseText.errors));
       return;
    }

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;
    
    // If there was an error adding to the cart, the cart hasn't changed,
    // so we skip re-rendering to prevent overwriting the error UI.
    if (event.detail?.data?.didError) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(this.sectionId, cartItemsHtml);
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
    
    this.#checkCuttingOrderState();
  };

  // --- Cutting Order Functionality ---
  #checkCuttingOrderState() {
    window.fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        cart.items.forEach(item => {
           const checkbox = this.querySelector(`.cutting-order-checkbox[data-variant="${item.variant_id}"]`);
           if (!checkbox) return;
           const instructionsContainer = checkbox.closest('.cart__items__item__details__cutting-order')?.querySelector('.cutting-instructions-container');
           if (instructionsContainer && item.properties && item.properties.cuttingOrder === 'true') {
             checkbox.checked = true;
             instructionsContainer.style.display = 'block';
             instructionsContainer.querySelectorAll('.cutting-instructions').forEach(el => el.required = true);
             const confirmationCheckbox = instructionsContainer.querySelector('.cutting-order-checkbox-confirmation');
             if (confirmationCheckbox && item.properties.cuttingOrderConfirmation === 'true') {
               confirmationCheckbox.checked = true;
             }
           }
        });
      })
      .catch(console.error);
  }

  #handleCuttingChange(event) {
    if (event.target.classList.contains('cutting-order-checkbox')) {
       const checkbox = event.target;
       const variantId = checkbox.dataset.variant;
       const isChecked = checkbox.checked;
       const instructionsContainer = checkbox.closest('.cart__items__item__details__cutting-order')?.querySelector('.cutting-instructions-container');
       if (instructionsContainer) {
          instructionsContainer.style.display = isChecked ? 'block' : 'none';
          instructionsContainer.querySelectorAll('.cutting-instructions').forEach(el => el.required = isChecked);
          if (!isChecked) {
             instructionsContainer.querySelectorAll('.cutting-instructions').forEach(el => el.value = '');
             const confCheckbox = instructionsContainer.querySelector('.cutting-order-checkbox-confirmation');
             if (confCheckbox) confCheckbox.checked = false;
          }
       }
       this.#syncCuttingProperties(variantId);
    } else if (event.target.classList.contains('cutting-order-checkbox-confirmation')) {
       const variantId = event.target.closest('.cutting-instructions-container')?.querySelector('.cutting-instructions')?.dataset.variant;
       if (variantId) this.#syncCuttingProperties(variantId);
    }
  }

  #debouncedCuttingSync = debounce((variantId) => this.#syncCuttingProperties(variantId), 500).bind(this);

  #handleCuttingInput(event) {
    if (event.target.classList.contains('cutting-instructions') || event.target.closest('.cut-to-size-sheet-fields')) {
       const instructionsContainer = event.target.closest('.cutting-instructions-container');
       if (!instructionsContainer) return;

       const totalSheetsInput = instructionsContainer.querySelector('[data-field="totalSheets"]');
       const numberOfOutsInput = instructionsContainer.querySelector('[data-field="numberOfOuts"]');
       const productUom = totalSheetsInput?.dataset?.productUom;
       const variantWidth = totalSheetsInput?.dataset?.variantWidth;
       
       const row = instructionsContainer.closest('tr');
       const qtyInput = row?.querySelector('input.cart__item-quantity');
       const quantity = parseInt(qtyInput?.value || '0', 10);

       if (productUom === 'SHEET' && totalSheetsInput && numberOfOutsInput) {
         const numberOfOuts = parseInt(numberOfOutsInput.value || '0', 10);
         totalSheetsInput.value = numberOfOuts * quantity;
       } else if (productUom !== 'SHEET' && totalSheetsInput) {
         const cutToSize = parseInt(instructionsContainer.querySelector('[data-field="cutToSize"]')?.value || '1', 10);
         if (cutToSize > 0 && variantWidth > 0) {
           totalSheetsInput.value = Math.floor(variantWidth / cutToSize);
         }
       }

       const variantId = event.target.dataset.variant || instructionsContainer.querySelector('.cutting-instructions')?.dataset.variant;
       if (variantId) this.#debouncedCuttingSync(variantId);
    }
  }

  #recalcCuttingSheets(line, quantity) {
     const row = this.refs.cartItemRows?.[line - 1];
     if (!row) return;
     const instructionsContainer = row.querySelector('.cutting-instructions-container');
     if (instructionsContainer && instructionsContainer.style.display !== 'none') {
        const totalSheetsInput = instructionsContainer.querySelector('[data-field="totalSheets"]');
        const numberOfOutsInput = instructionsContainer.querySelector('[data-field="numberOfOuts"]');
        const productUom = totalSheetsInput?.dataset?.productUom;
        const variantWidth = totalSheetsInput?.dataset?.variantWidth;
        if (productUom === 'SHEET' && totalSheetsInput && numberOfOutsInput) {
           const numberOfOuts = parseInt(numberOfOutsInput.value || '0', 10);
           totalSheetsInput.value = numberOfOuts * quantity;
        } else if (productUom !== 'SHEET' && totalSheetsInput) {
           const cutToSize = parseInt(instructionsContainer.querySelector('[data-field="cutToSize"]')?.value || '1', 10);
           if (cutToSize > 0 && variantWidth > 0) {
             totalSheetsInput.value = Math.floor(variantWidth / cutToSize);
           }
        }
        const variantId = instructionsContainer.querySelector('.cutting-instructions')?.dataset.variant;
        if (variantId) this.#debouncedCuttingSync(variantId);
     }
  }

  #syncCuttingProperties(variantId) {
    const checkbox = this.querySelector(`.cutting-order-checkbox[data-variant="${variantId}"]`);
    if (!checkbox) return;
    const isChecked = checkbox.checked;
    const instructionsContainer = checkbox.closest('.cart__items__item__details__cutting-order')?.querySelector('.cutting-instructions-container');

    window.fetch('/cart.js')
      .then(res => res.json())
      .then(cart => {
        const item = cart.items.find(i => String(i.variant_id) === String(variantId));
        if (!item) return;

        let cuttingProperties = {};
        if (isChecked && instructionsContainer) {
          cuttingProperties.cuttingOrder = 'true';
          const widthInput = instructionsContainer.querySelector('[data-sheet-width="true"]');
          const lengthInput = instructionsContainer.querySelector('[data-sheet-length="true"]');
          if (widthInput && lengthInput) {
            const w = widthInput.value || '';
            const l = lengthInput.value || '';
            if (w && l) cuttingProperties.cutToSize = w + ' x ' + l;
            else if (w) cuttingProperties.cutToSize = w;
            else if (l) cuttingProperties.cutToSize = l;
          } else {
            cuttingProperties.cutToSize = instructionsContainer.querySelector('[data-field="cutToSize"]')?.value || '';
          }
          cuttingProperties.numberOfOuts = instructionsContainer.querySelector('[data-field="numberOfOuts"]')?.value || '';
          cuttingProperties.totalSheets = instructionsContainer.querySelector('[data-field="totalSheets"]')?.value || '';
          cuttingProperties.packagingInstructions = instructionsContainer.querySelector('[data-field="packagingInstructions"]')?.value || '';
          cuttingProperties.cuttingOrderConfirmation = instructionsContainer.querySelector('.cutting-order-checkbox-confirmation')?.checked ? 'true' : 'false';
        }

        const newProps = { ...item.properties };
        const propsToRemove = ['cuttingOrder', 'cutToSize', 'numberOfOuts', 'totalSheets', 'packagingInstructions', 'cuttingOrderConfirmation'];
        if (!isChecked) {
          propsToRemove.forEach(p => { newProps[p] = null; });
        } else {
          propsToRemove.forEach(p => { newProps[p] = cuttingProperties[p] || ''; });
        }

        window.fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ line: cart.items.indexOf(item) + 1, properties: newProps })
        });
      });
  }
  // --- End Cutting Order Functionality ---

  /**
   * Disables the cart items.
   */
  /**
   * Show an MOQ confirmation modal as a native <dialog> appended to <body>.
   * This puts it in the browser's top layer, guaranteed above the Cart Drawer.
   * @param {string} message
   * @param {function(boolean): void} callback
   */
  #showMoqConfirm(message, callback) {
    if (window.vex && window.vex.dialog) {
      let appendLoc = 'body';
      let wrapperDialog = null;
      if (document.querySelector('.cart-drawer__dialog[open]')) {
        wrapperDialog = document.createElement('dialog');
        const uniqueId = 'moq-vex-wrapper-cart-' + Date.now();
        wrapperDialog.id = uniqueId;
        wrapperDialog.style.cssText = 'width: 100vw; height: 100vh; background: transparent; border: none; padding: 0; margin: 0; max-width: none; max-height: none; overflow: hidden;';
        document.body.appendChild(wrapperDialog);
        wrapperDialog.showModal();
        appendLoc = '#' + uniqueId;
      }
      window.vex.dialog.confirm({
        message: message,
        appendLocation: appendLoc,
        callback: function (value) {
          callback(value);
          if (wrapperDialog) {
            setTimeout(() => {
              if (wrapperDialog.open) wrapperDialog.close();
              wrapperDialog.remove();
            }, 500);
          }
        }
      });
    } else {
      const accepted = window.confirm(message);
      callback(accepted);
    }
  }

  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}